import React, { useEffect, useState } from "react";
import { Card, Button, Chip, ProgressRing } from "../ui";
import { Check, Path, contentIcon } from "../icons";
import CertificateButton from "./CertificateButton";
import LearningPathFeedbackDialog from "./LearningPathFeedbackDialog";
import { TYPE_LABEL, isOverdue, formatDueDate, openLabel } from "../../lib/learning";
import { hasSubmittedPathFeedback } from "../../services/AssignmentService";

/**
 * A learning path shown as what it actually is — an ordered journey.
 *
 * The steps are a timeline rather than a list of rows: the connector runs green
 * through everything finished and greys out at the first incomplete step, so
 * "where am I" is answered before any text is read. Step order, status and
 * availability all come straight from `/api/assignment/me`; nothing here
 * re-derives completion.
 */
export const LearningPathCard = ({ item, onOpenStep }) => {
  const { done = 0, total = 0 } = item.progress || {};
  const pct = total > 0 ? Math.round((done / total) * 100) : 0;
  const overdue = isOverdue(item);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackGiven, setFeedbackGiven] = useState(true);

  useEffect(() => {
    let live = true;
    if (item.status === "completed") {
      hasSubmittedPathFeedback(item.contentId).then((submitted) => {
        if (live) setFeedbackGiven(submitted);
      });
    }
    return () => {
      live = false;
    };
  }, [item.status, item.contentId]);

  return (
    <Card>
      <div className="ds-path__head">
        <span className="ds-tile ds-tile--brand">
          <Path size={19} />
        </span>

        <div className="ds-path__title">
          <h3 className="ds-h3 ds-truncate">{item.title}</h3>
          <p className="ds-sub" style={{ marginTop: 2 }}>
            Learning path · {total} step{total === 1 ? "" : "s"}
            {item.dueDate && (
              <span style={{ color: overdue ? "var(--danger-ink)" : undefined }}>
                {" · "}
                {overdue ? "Overdue " : "Due "}
                {formatDueDate(item.dueDate)}
              </span>
            )}
          </p>
        </div>

        <div className="ds-path__actions">
          <ProgressRing
            value={pct}
            size={44}
            thickness={4}
            tone={pct === 100 ? "success" : "brand"}
          />
          {item.status === "completed" && (
            <CertificateButton
              contentType="learning-path"
              contentId={item.contentId}
              title={item.title}
              onDownloaded={() => {
                if (!feedbackGiven) setFeedbackOpen(true);
              }}
            />
          )}
        </div>
      </div>

      <LearningPathFeedbackDialog
        open={feedbackOpen}
        learningPathId={item.contentId}
        pathTitle={item.title}
        onClose={() => {
          setFeedbackOpen(false);
          setFeedbackGiven(true);
        }}
      />

      <div className="ds-steps">
        {item.steps?.length ? (
          item.steps.map((step, idx) => {
            const isDone = step.status === "completed";
            const StepIcon = contentIcon(step.contentType);
            return (
              <div
                key={`${step.contentType}-${step.contentId}-${idx}`}
                className={`ds-step${isDone ? " ds-step--done" : ""}`}
              >
                <span
                  className={[
                    "ds-step__node",
                    isDone && "ds-step__node--done",
                    !isDone && step.status === "in-progress" && "ds-step__node--active",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                >
                  {isDone ? <Check size={12} /> : idx + 1}
                </span>

                <Chip tone={isDone ? "brand" : "neutral"}>
                  {TYPE_LABEL[step.contentType] || step.contentType}
                </Chip>

                <span className="ds-step__title" title={step.title}>
                  {step.title}
                </span>

                {step.percent > 0 && step.percent < 100 && (
                  <span className="ds-sub ds-shrink-0" style={{ margin: 0 }}>
                    {step.percent}%
                  </span>
                )}

                <Button
                  variant="outline"
                  size="sm"
                  icon={StepIcon}
                  disabled={!step.available}
                  // The step carries its path, so the work done in it is
                  // credited to THIS path's run rather than to the learner's
                  // history at large.
                  onClick={() => onOpenStep({ ...step, pathId: item.contentId })}
                >
                  {step.available ? openLabel(step.status) : "Unavailable"}
                </Button>
              </div>
            );
          })
        ) : (
          <p className="ds-sub" style={{ padding: "8px 0" }}>
            This path has no steps yet.
          </p>
        )}
      </div>
    </Card>
  );
};

export default LearningPathCard;
